import React, { useCallback } from 'react'
import isEqual from 'react-fast-compare'
import type { Playlist, PlaylistId } from 'types/playlist'
import PlaylistArtwork from 'components/common/playlist-artwork'
import type { LinkComponentType } from 'components/ui/grid-item'
import GridItem from 'components/ui/grid-item'
import Lozenge from 'components/ui/lozenge'

const formatTrackCount = (count: number): string =>
  count === 1 ? `${count} track` : `${count} tracks`

const formatDuration = (duration?: number): string | null | undefined => {
  if (!duration) return null
  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  return hours > 0
    ? `${hours} ${hours > 1 ? 'hrs' : 'hr'} ${minutes} mins`
    : `${minutes} mins`
}

type Props = {
  color?: string | null | undefined
  image?: string | null | undefined
  link?: LinkComponentType
  overlayActions?: React.ReactNode
  overlayVisible?: boolean
  playlist: Playlist
  status?: string | null | undefined
  onClick?: (id: PlaylistId) => void
  optimizeImage?: boolean
}

/**
 * Grid item component for displaying playlist information with artwork and metadata
 * @param color - Override color for the playlist artwork background
 * @param image - Override image URL for the playlist artwork
 * @param link - Link component configuration for navigation
 * @param overlayActions - Actions to display in the overlay on hover
 * @param overlayVisible - Whether to show the overlay
 * @param playlist - Playlist data to display
 * @param status - Status text to display as a lozenge
 * @param onClick - Click handler for the grid item
 * @param optimizeImage - Whether to use optimized image loading
 */
const PlaylistGridItem = ({
  color,
  image,
  link,
  overlayActions,
  overlayVisible,
  playlist,
  status,
  onClick,
  optimizeImage,
}: Props) => {
  const trackCount: string = formatTrackCount(playlist['track_count'])
  const duration: string | null | undefined = formatDuration(
    playlist['duration'],
  )
  const handleClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()

      if (onClick) {
        onClick(playlist['playlist_id'])
      }
    },
    [onClick, playlist['playlist_id']],
  )
  const overlay: React.ReactNode = (
    <GridItem.Overlay actions={overlayActions} visible={overlayVisible} />
  )
  const media: React.ReactNode = (
    <PlaylistArtwork
      color={color === undefined ? playlist['color'] : color}
      image={image === undefined ? playlist['image_url'] : image} // TODO: Refactor handling (overlay shouldn't render only if set to false)
      overlay={overlayActions === false ? null : overlay}
      status={status && <Lozenge color="yellow" text={status} />}
      trackArtworks={image === '' ? [] : playlist['tracks_artworks']}
      optimizeImage={optimizeImage}
    />
  )
  return (
    <GridItem
      link={link}
      media={media}
      title={playlist['name'] || 'Untitled playlist'}
      caption={[trackCount, duration].filter(Boolean).join(', ')}
      onClick={onClick ? handleClick : undefined}
    />
  )
}

export default React.memo<Props>(PlaylistGridItem, isEqual)
